<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Projetos Atrasados</h3>
            <GraficoInfoIcon 
                titulo="Projetos Atrasados"
                descricao="Mostra projetos que estão atrasados em relação às datas previstas, destacando aqueles que necessitam de atenção imediata."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoProjetosAtrasados',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    computed: {
        projetosAtrasados() {
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            
            return this.projetos
                .filter(p => {
                    // Requer PCM com dtEstimadaFinal
                    if (!p.pcm || !p.pcm.dtEstimadaFinal) return false;
                    
                    const dtEstimadaFinal = p.pcm.dtEstimadaFinal;
                    const termino = new Date(dtEstimadaFinal);
                    termino.setHours(0, 0, 0, 0);
                    
                    // Projeto está atrasado se a data limite de implementação já passou e não está encerrado
                    if (termino < hoje && !p.encerrado) {
                        const diasAtraso = Math.ceil((hoje - termino) / (1000 * 60 * 60 * 24));
                        p.diasAtraso = diasAtraso;
                        return true;
                    }
                    
                    return false;
                })
                .sort((a, b) => (b.diasAtraso || 0) - (a.diasAtraso || 0))
                .slice(0, 20);
        },
        temDados() {
            return this.projetosAtrasados.length > 0;
        },
        chartData() {
            const projetos = this.projetosAtrasados;
            return {
                labels: projetos.map(p => {
                    const codigoPCM = p.pcm?.codigo || '';
                    const nome = p.nome || 'Sem nome';
                    const label = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
                    return label.length > 25 ? label.substring(0, 25) + '...' : label;
                }),
                datasets: [{
                    label: 'Dias de Atraso',
                    data: projetos.map(p => p.diasAtraso || 0),
                    backgroundColor: 'rgba(231, 76, 60, 0.6)',
                    borderColor: 'rgba(231, 76, 60, 1)',
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            return {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const projeto = this.projetosAtrasados[index];
                        if (projeto) {
                            self.$emit('clicar-projeto', projeto.id);
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Dias de Atraso'
                        },
                        ticks: {
                            stepSize: 1
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                return `Atraso: ${context.parsed.x} dias`;
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

